import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Landing() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [attestations, setAttestations] = useState([]);
    const [stats, setStats] = useState({ total: 0, pending: 0, active: 0, retired: 0 });
    const [loading, setLoading] = useState(true);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [latest, setLatest] = useState({ power_kwh: 0, timestamp: '' });
    const [globalStats, setGlobalStats] = useState({ total_energy_mwh: 0, total_records: 0, live_plants: 0 });
    const [history, setHistory] = useState([]);
    const [user, setUser] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 6;

    useEffect(() => {
        const uJson = localStorage.getItem('user');
        if (uJson) setUser(JSON.parse(uJson));
        
        const fetchData = async () => {
            try {
                const [resA, resL, resG] = await Promise.all([
                    fetch('/api/attestations'),
                    fetch('/api/sensor/latest'),
                    fetch('/api/stats/global')
                ]);
                const dataA = await resA.json();
                const dataL = await resL.json();
                const dataG = await resG.json();

                if (Array.isArray(dataA)) {
                    // Sorting newest first is critical for 'real-time' feeling
                    const sorted = dataA.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                    setAttestations(sorted); 
                    setStats({
                        total: dataA.length,
                        pending: dataA.filter(a => a.status === 'pending_expert').length,
                        active: dataA.filter(a => a.status === 'active').length,
                        retired: dataA.filter(a => a.status === 'retired').length
                    });
                }
                setLatest(dataL);
                setGlobalStats(dataG);
                setLoading(false);
            } catch (err) {
                console.error('Fetch failed:', err);
                setLoading(false);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 5000); 
        return () => clearInterval(interval);
    }, []);

    const showHistory = async (id) => {
        try {
            const res = await fetch(`/api/attestations/${encodeURIComponent(id)}/history`);
            const data = await res.json();
            if (Array.isArray(data)) {
                setHistory(data.reverse());
                setSelectedAsset(id);
            } else {
                setHistory([]);
                setSelectedAsset(id);
            }
        } catch (err) {
            console.error('History fetch failed:', err);
            setHistory([]);
            setSelectedAsset(id);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0c10] text-slate-300 font-sans">
            {/* Background */}
            <div className="fixed inset-0 pointer-events-none 
                            overflow-hidden z-0">
                <div className="absolute inset-0 
                                bg-[radial-gradient(ellipse_at_top_right,rgba(15,23,20,0.8)_0%,transparent_60%)]">
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-px 
                                bg-gradient-to-r from-transparent 
                                via-emerald-900/30 to-transparent">
                </div>
            </div>

            {/* Nav */}
            <header className="border-b border-white/5 mb-16 relative z-10">
                <div className="container mx-auto px-6">
                    <div className="flex justify-between items-center h-16">
                        
                        {/* Logo */}
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                                <svg className="w-7 h-7 text-emerald-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M2 20L8 8L13 16" />
                                    <path d="M8 20L15 6L22 20" />
                                    <path d="M8 8L15 6" strokeDasharray="1.5 1.5" />
                                    <circle cx="15" cy="6" r="1.5" fill="currentColor" />
                                </svg>
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-white leading-none tracking-tight">
                                    Silicon Himalayas Ledger
                                </p>
                                <p className="text-[9px] text-slate-500 uppercase tracking-widest font-medium mt-0.5">
                                    Green Energy Certificate Platform
                                </p>
                            </div>
                            <span className="text-[9px] text-slate-600 ml-2 pl-2 border-l border-white/10 hidden sm:inline-block">
                                A Silicon Himalayas Product
                            </span>
                        </Link>

                        {/* Desktop Nav links */}
                        <nav className="hidden sm:flex items-center gap-1">
                            <Link to="/verify" className="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white transition">
                                Verify Certificate
                            </Link>
                            <Link to="/investors" className="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white transition">
                                Investors
                            </Link>
                            <a href="https://silicon-himalayas.com" target="_blank" rel="noopener noreferrer" className="px-4 py-2 text-xs font-medium text-slate-400 hover:text-white transition">
                                About
                            </a>
                            <div className="w-px h-4 bg-white/10 mx-2"></div>
                            {user ? (
                                <Link to={
                                    user.role === 'admin' ? '/admin' 
                                    : user.role === 'expert' ? '/dashboard/expert' 
                                    : user.role === 'investor' ? '/dashboard/investor' 
                                    : user.role === 'observer' ? '/dashboard/observer' 
                                    : '/dashboard'
                                } className="px-4 py-2 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border border-emerald-600/20 rounded-lg text-xs font-semibold transition flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                    {user.role === 'expert' ? 'Verifier Portal' : `${user.role?.charAt(0).toUpperCase() + user.role?.slice(1)} Portal`}
                                </Link>
                            ) : (
                                <>
                                    <Link to="/login" className="px-4 py-2 text-xs font-medium text-slate-300 hover:text-white transition">
                                        Sign In
                                    </Link>
                                    <Link to="/register" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-semibold transition">
                                        Register
                                    </Link>
                                </>
                            )}
                        </nav>

                        {/* Hamburger Button (Mobile) */}
                        <button 
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="sm:hidden p-2 text-slate-400 hover:text-white transition focus:outline-none"
                            aria-label="Toggle menu"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {mobileMenuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                )}
                            </svg>
                        </button>
                    </div>

                    {/* Mobile Menu Panel */}
                    {mobileMenuOpen && (
                        <div className="sm:hidden border-t border-white/5 py-4 flex flex-col gap-2 animate-fade-in relative z-20 bg-[#0a0c10]/95 backdrop-blur-md rounded-b-xl px-2">
                            <Link 
                                to="/verify" 
                                onClick={() => setMobileMenuOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white transition"
                            >
                                Verify Certificate
                            </Link>
                            <Link 
                                to="/investors" 
                                onClick={() => setMobileMenuOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white transition"
                            >
                                Investors
                            </Link>
                            <a 
                                href="https://silicon-himalayas.com" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={() => setMobileMenuOpen(false)}
                                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white transition"
                            >
                                About
                            </a>
                            <div className="h-px bg-white/10 my-1 mx-4"></div>
                            {user ? (
                                <Link 
                                    to={
                                        user.role === 'admin' ? '/admin' 
                                        : user.role === 'expert' ? '/dashboard/expert' 
                                        : user.role === 'investor' ? '/dashboard/investor' 
                                        : user.role === 'observer' ? '/dashboard/observer' 
                                        : '/dashboard'
                                    } 
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="mx-4 px-4 py-3 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 border border-emerald-600/20 rounded-lg text-sm font-semibold transition flex items-center justify-center gap-2"
                                >
                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                    {user.role === 'expert' ? 'Verifier Portal' : `${user.role?.charAt(0).toUpperCase() + user.role?.slice(1)} Portal`}
                                </Link>
                            ) : (
                                <div className="flex flex-col gap-2 px-4 mt-2">
                                    <Link 
                                        to="/login" 
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="py-2.5 text-sm font-medium text-slate-300 hover:text-white text-center transition border border-white/10 rounded-lg"
                                    >
                                        Sign In
                                    </Link>
                                    <Link 
                                        to="/register" 
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-semibold text-center transition"
                                    >
                                        Register
                                    </Link>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </header>

            <main className="container mx-auto px-6 pb-32 relative z-10">
                {/* Hero Section */}
                <section className="mb-20">
                    <div className="max-w-3xl">
                        
                        {/* Status pill */}
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/8 border border-emerald-500/15 rounded-full mb-8">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                            <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400">
                                NRB Regulatory Sandbox · Pending
                            </span>
                        </div>

                        <h2 className="text-5xl lg:text-6xl font-bold mb-6 tracking-tight leading-[1.05] text-white">
                            Nepal's Renewable Energy,<br/>
                            <span className="text-emerald-400">
                                Verified for Global Markets
                            </span>
                        </h2>
                        
                        <p className="text-slate-400 text-lg mb-10 leading-relaxed max-w-2xl">
                            Silicon Himalayas Ledger is a regulated, blockchain-based platform for issuing and trading I-REC renewable energy certificates from Nepali solar and hydropower projects — connecting local clean energy to global corporate ESG demand.
                        </p>

                        {/* Trust bar — no emojis, text only */}
                        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-10 text-[10px] text-slate-500 uppercase tracking-widest font-semibold">
                            <span>Built for Nepal</span>
                            <span className="text-white/10">·</span>
                            <span>I-REC Standard</span>
                            <span className="text-white/10">·</span>
                            <span>Hyperledger Fabric</span>
                            <span className="text-white/10">·</span>
                            <span>NRB Sandbox</span>
                            <span className="text-white/10">·</span>
                            <span>GCC · Evident Registry</span>
                        </div>

                        {/* CTAs */}
                        <div className="flex flex-wrap gap-4 items-center">
                            <button
                                onClick={() => window.scrollTo({
                                    top: document.getElementById('explorer')?.offsetTop - 100, 
                                    behavior: 'smooth'
                                })}
                                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold text-sm transition shadow-lg shadow-emerald-900/40">
                                View Live Ledger
                            </button>
                            <Link to="/investors" className="px-6 py-3 border border-white/10 hover:border-white/20 hover:bg-white/3 text-slate-300 rounded-lg font-semibold text-sm transition">
                                Investment Opportunity
                            </Link>
                            <a href="https://silicon-himalayas.com" target="_blank" rel="noopener noreferrer" className="text-xs text-slate-500 hover:text-slate-400 transition">
                                silicon-himalayas.com ↗
                            </a>
                        </div>
                    </div>
                </section>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border border-white/8 rounded-xl overflow-hidden mb-20 gap-px bg-white/8">
                    {[
                        { 
                            label: 'Live Generation', 
                            value: `${latest.power_kwh || latest.increment_kwh || 0}`,
                            unit: 'kWh',
                            live: true 
                        },
                        { 
                            label: 'MWh Recorded', 
                            value: globalStats.total_energy_mwh,
                            unit: 'MWh'
                        },
                        { 
                            label: 'Certificates', 
                            value: stats.total,
                            unit: null 
                        },
                        { 
                            label: 'Pending Review', 
                            value: stats.pending,
                            unit: null,
                            warn: stats.pending > 0 
                        },
                        { 
                            label: 'Retired', 
                            value: stats.retired,
                            unit: null 
                        },
                        { 
                            label: 'Registry', 
                            value: 'GCC · Evident',
                            unit: 'I-REC(E)',
                            text: true 
                        }
                    ].map((s, i) => (
                        <div key={i} className="px-4 py-5 bg-[#0a0c10] hover:bg-white/4 transition col-span-1">
                            <div className="flex items-baseline gap-1.5 mb-1">
                                {s.live && (
                                    <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse flex-shrink-0 mb-0.5"></span>
                                )}
                                <span className={`font-semibold leading-none ${s.text ? 'text-sm text-slate-300' : 'text-xl text-white'} ${s.live ? 'text-emerald-400' : ''} ${s.warn ? 'text-amber-400' : ''}`}>
                                    {s.value}
                                </span>
                                {s.unit && (
                                    <span className="text-[10px] text-slate-500 font-medium">
                                        {s.unit}
                                    </span>
                                )}
                            </div>
                            <p className="text-[9px] uppercase tracking-widest text-slate-600 font-semibold">
                                {s.label}
                            </p>
                        </div>
                    ))}
                </div>

                {/* Pipeline Project Section */}
                <section className="mb-20">
                    {/* Section label */}
                    <div className="flex items-center gap-3 mb-6">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                            Pipeline Project
                        </p>
                        <div className="flex-1 h-px bg-white/5"></div>
                    </div>

                    <div className="border border-white/8 rounded-xl bg-white/2 p-8">
                        {/* Project header */}
                        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8 pb-8 border-b border-white/5">
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <span className="text-[9px] font-semibold px-2.5 py-1 rounded-md bg-amber-500/8 text-amber-400 border border-amber-500/15 uppercase tracking-widest">
                                        Solar · 10 MW
                                    </span>
                                    <span className="text-[9px] font-semibold px-2.5 py-1 rounded-md bg-white/5 text-slate-400 border border-white/8 uppercase tracking-widest">
                                        Onboarding
                                    </span>
                                </div>
                                <h4 className="text-2xl font-semibold text-white mb-1 tracking-tight">
                                    Ramechhap Solar Farm
                                </h4>
                                <p className="text-slate-500 text-sm">
                                    Bagmati Province, Nepal · Grid-connected · NEA Power Purchase Agreement
                                </p>
                            </div>
                            <a href="https://silicon-himalayas.com/projects" target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-500 hover:text-slate-400 transition uppercase tracking-widest font-semibold whitespace-nowrap">
                                All projects ↗
                            </a>
                        </div>

                        {/* Stats row */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                            {[
                                { label: 'Capacity', value: '10 MW' },
                                { label: 'Est. Annual Generation', value: '~15,500 MWh' },
                                { label: 'I-RECs / Year', value: '~15,500' },
                                { label: 'I-REC Issuer', value: 'GCC · Evident' }
                            ].map((s, i) => (
                                <div key={i}>
                                    <p className="text-[9px] uppercase tracking-widest text-slate-600 font-semibold mb-1">
                                        {s.label}
                                    </p>
                                    <p className="text-sm font-semibold text-white">
                                        {s.value}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Pipeline stepper */}
                        <div className="flex items-center overflow-x-auto pb-1">
                            {[
                                { step: '1', label: 'SPV Structured', status: 'complete' },
                                { step: '2', label: 'GCC Registration', status: 'active' },
                                { step: '3', label: 'I-REC Issuance', status: 'pending' },
                                { step: '4', label: 'Listed on Ledger', status: 'pending' },
                                { step: '5', label: 'Available to Buyers', status: 'pending' }
                            ].map((s, i, arr) => (
                                <React.Fragment key={i}>
                                    <div className="flex flex-col items-center min-w-[72px]">
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border ${
                                            s.status === 'complete' 
                                                ? 'bg-emerald-600 border-emerald-600 text-white' 
                                                : s.status === 'active' 
                                                ? 'bg-transparent border-emerald-500 text-emerald-400' 
                                                : 'bg-transparent border-white/10 text-slate-600'
                                        }`}>
                                            {s.status === 'complete' ? '✓' : s.step}
                                        </div>
                                        <p className={`text-[8px] mt-2 text-center font-semibold uppercase tracking-wide leading-tight ${
                                            s.status === 'complete' 
                                                ? 'text-emerald-500' 
                                                : s.status === 'active' 
                                                ? 'text-emerald-400' 
                                                : 'text-slate-600'
                                        }`}>
                                            {s.label}
                                        </p>
                                    </div>
                                    {i < arr.length - 1 && (
                                        <div className={`h-px flex-1 mx-2 mb-5 ${
                                            s.status === 'complete' 
                                                ? 'bg-emerald-600/40' 
                                                : 'bg-white/5'
                                        }`}>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        <p className="text-[10px] text-slate-600 mt-6 font-medium">
                            GCC/Evident I-REC registration in progress · Silicon Himalayas acting as designated Registrant · Generation data available Q3 2027
                        </p>
                    </div>
                </section>

                {/* How It Works Section */}
                <section className="mb-20">
                    <div className="flex items-center gap-3 mb-8">
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                            How It Works
                        </p>
                        <div className="flex-1 h-px bg-white/5"></div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                        {[
                            {
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                                    </svg>
                                ),
                                step: '01',
                                title: 'Generate',
                                body: 'A Nepali solar or hydropower project produces clean electricity. Generation data is captured via metered sensors and recorded on-chain.'
                            },
                            {
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                                    </svg>
                                ),
                                step: '02',
                                title: 'Certify',
                                body: 'Silicon Himalayas committee verifies the data. GCC issues an official I-REC certificate on the Evident Registry — 1 certificate per MWh generated.'
                            },
                            {
                                icon: (
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                ),
                                step: '03',
                                title: 'Trade & Retire',
                                body: 'Global companies buy and retire certificates for Scope 2 ESG reporting. Foreign currency flows directly to Nepali project developers.'
                            }
                        ].map((s, i) => (
                            <div key={i} className="border border-white/8 rounded-xl bg-white/2 p-6 hover:bg-white/4 transition">
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-9 h-9 rounded-lg bg-emerald-500/8 border border-emerald-500/15 flex items-center justify-center text-emerald-400">
                                        {s.icon}
                                    </div>
                                    <span className="text-[10px] font-bold text-slate-700 uppercase tracking-widest">
                                        {s.step}
                                    </span>
                                </div>
                                <h4 className="font-semibold text-white mb-2 text-base">
                                    {s.title}
                                </h4>
                                <p className="text-sm text-slate-500 leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Explorer Section */}
                <div id="explorer" className="mb-20">
                    <div className="mb-8 flex justify-between items-end">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                                Live Ledger
                            </p>
                            <h3 className="text-2xl font-semibold text-white tracking-tight">
                                Certificate Explorer
                            </h3>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                            <span className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest">
                                Live sync
                            </span>
                        </div>
                    </div>

                    {loading ? (
                        <div className="p-20 text-center border border-white/8 rounded-xl bg-white/2 opacity-50 text-slate-400 font-semibold">
                            Synchronizing with peer nodes...
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {attestations.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((a, i) => (
                                <div key={i} className="border border-white/8 rounded-xl bg-white/2 hover:bg-white/4 hover:border-white/12 transition overflow-hidden">
                                    {/* Card top bar */}
                                    <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                                        <div className="flex items-center gap-1.5 min-w-0 max-w-[70%]">
                                            <span 
                                                title={a.id} 
                                                className="font-mono text-[9px] text-slate-500 truncate hover:text-slate-350 cursor-pointer select-all"
                                            >
                                                {a.id}
                                            </span>
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigator.clipboard.writeText(a.id);
                                                    const btn = e.currentTarget;
                                                    const origHTML = btn.innerHTML;
                                                    btn.innerHTML = '<span class="text-emerald-400 text-[8px] font-black uppercase">Copied</span>';
                                                    setTimeout(() => { btn.innerHTML = origHTML; }, 1500);
                                                }}
                                                title="Copy full attestation ID"
                                                className="p-1 hover:bg-white/5 rounded text-slate-650 hover:text-emerald-400 transition flex-shrink-0"
                                            >
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                                                </svg>
                                            </button>
                                        </div>
                                        <span className={`text-[9px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-md ${
                                            a.status === 'active' 
                                                ? 'bg-emerald-500/8 text-emerald-400 border border-emerald-500/15' 
                                                : a.status === 'retired' 
                                                ? 'bg-blue-500/8 text-blue-400 border border-blue-500/15' 
                                                : a.status === 'rejected'
                                                ? 'bg-rose-500/8 text-rose-400 border border-rose-500/15'
                                                : 'bg-amber-500/8 text-amber-400 border border-amber-500/15'
                                        }`}>
                                            {a.status?.replace('_', ' ')}
                                        </span>
                                    </div>

                                    {/* Card body */}
                                    <div className="p-5">
                                        <div className="mb-4">
                                            <span className="text-3xl font-bold text-white tracking-tight">
                                                {a.mwh || a.docType}
                                            </span>
                                            {!a.isPhysical && (
                                                <span className="text-sm text-slate-500 ml-1.5 font-medium">
                                                    MWh
                                                </span>
                                            )}
                                            <p className="text-xs text-slate-500 mt-1">
                                                {a.projectId ? (
                                                    <Link to={`/plant/${a.projectId}`} className="text-emerald-400 hover:underline hover:text-emerald-350 transition-colors font-semibold">
                                                        {a.plantName || a.projectId}
                                                    </Link>
                                                ) : (
                                                    'Nepal · Solar / Hydro'
                                                )}
                                            </p>
                                        </div>

                                        <div className="space-y-1.5 mb-4">
                                            <div className="flex justify-between text-[10px]">
                                                <span className="text-slate-600 font-semibold uppercase tracking-widest">
                                                    Sensor
                                                </span>
                                                <span className="text-slate-400 font-mono">
                                                    {(a.sensorDid || a.issuer || '').substring(0, 14)}...
                                                </span>
                                            </div>
                                            {a.expertDid && (
                                                <div className="flex justify-between text-[10px]">
                                                    <span className="text-slate-600 font-semibold uppercase tracking-widest">
                                                        Verifier
                                                    </span>
                                                    <span className="text-emerald-500 font-mono">
                                                        {a.expertDid.substring(0, 14)}...
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <button 
                                            onClick={() => showHistory(a.id)}
                                            className="w-full py-2.5 border border-white/8 hover:border-white/15 hover:bg-white/4 text-slate-400 hover:text-white rounded-lg text-[10px] font-semibold uppercase tracking-widest transition"
                                        >
                                            View Audit Trail
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination Controls */}
                    {!loading && attestations.length > pageSize && (
                        <div className="mt-12 flex justify-center items-center gap-4">
                            <button 
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="px-6 py-2 border border-white/8 hover:bg-white/5 text-xs font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition"
                            >
                                Previous
                            </button>
                            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                                Page {currentPage} of {Math.ceil(attestations.length / pageSize)}
                            </span>
                            <button 
                                onClick={() => setCurrentPage(p => Math.min(Math.ceil(attestations.length / pageSize), p + 1))}
                                disabled={currentPage >= Math.ceil(attestations.length / pageSize)}
                                className="px-6 py-2 border border-white/8 hover:bg-white/5 text-xs font-medium text-slate-300 disabled:opacity-30 disabled:cursor-not-allowed rounded-lg transition"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <footer className="border-t border-white/5 pt-12 pb-16 mt-20">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10">
                        
                        <div>
                            <p className="text-xs font-semibold text-white mb-3">
                                Silicon Himalayas Ledger
                            </p>
                            <p className="text-xs text-slate-500 leading-relaxed mb-3">
                                Regulated blockchain infrastructure for Nepal's renewable energy certificate market. NRB sandbox engaged.
                            </p>
                            <a href="https://silicon-himalayas.com"
                               target="_blank" rel="noopener noreferrer"
                               className="text-[10px] text-slate-600 hover:text-slate-400 transition uppercase tracking-widest font-semibold"
                            >
                                A Silicon Himalayas Product ↗
                            </a>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-3">
                                Registry & Standards
                            </p>
                            <div className="space-y-2">
                                <a href="https://www.gcc.re" target="_blank" rel="noopener noreferrer" className="block text-xs text-slate-500 hover:text-slate-300 transition">
                                    GCC — Accredited I-REC Issuer, Nepal ↗
                                </a>
                                <a href="https://evident.global" target="_blank" rel="noopener noreferrer" className="block text-xs text-slate-500 hover:text-slate-300 transition">
                                    Evident Registry (Xpansiv) ↗
                                </a>
                                <a href="https://www.trackingstandard.org" target="_blank" rel="noopener noreferrer" className="block text-xs text-slate-500 hover:text-slate-300 transition">
                                    I-TRACK Foundation ↗
                                </a>
                            </div>
                        </div>

                        <div>
                            <p className="text-xs font-semibold text-slate-400 mb-3">
                                Platform
                            </p>
                            <div className="space-y-2">
                                <Link to="/investors" className="block text-xs text-slate-500 hover:text-slate-300 transition">
                                    Investment Opportunity
                                </Link>
                                <Link to="/verify" className="block text-xs text-slate-500 hover:text-slate-300 transition">
                                    Verify Certificate
                                </Link>
                                <Link to="/login" className="block text-xs text-slate-500 hover:text-slate-300 transition">
                                    Platform Sign In
                                </Link>
                                <a href="https://silicon-himalayas.com/projects" target="_blank" rel="noopener noreferrer" className="block text-xs text-slate-500 hover:text-slate-300 transition">
                                    Project Pipeline ↗
                                </a>
                                <a href="mailto:office@silicon-himalayas.com" className="block text-xs text-slate-500 hover:text-slate-300 transition">
                                    Contact
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-white/5 pt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
                        <p className="text-[9px] uppercase tracking-widest text-slate-700 font-semibold">
                            © 2026 Silicon Himalayas · Kathmandu, Nepal · Reg No: OCR 82/83-19-101587512
                        </p>
                        <p className="text-[9px] uppercase tracking-widest text-slate-700">
                            Hyperledger Fabric v2.5 · I-REC(E) · NRB Sandbox Phase
                        </p>
                    </div>
                </footer>
            </main>

            {/* History Modal */}
            {selectedAsset && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 animate-fade-in">
                    <div className="absolute inset-0 bg-[#080a0f]/90 backdrop-blur-xl" onClick={() => setSelectedAsset(null)}></div>
                    <div className="border border-white/8 bg-[#0a0c10] w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl relative z-10">
                        <div className="p-10 border-b border-white/10 flex justify-between items-center sticky top-0 bg-[#080a0f]/50 backdrop-blur-xl z-20">
                            <div>
                                <div className="flex items-center gap-3 mb-1">
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                    <h3 className="text-2xl font-bold tracking-tight text-white">Immutable Audit Trail</h3>
                                </div>
                                <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Certificate Audit Trail</p>
                            </div>
                            <button onClick={() => setSelectedAsset(null)} className="p-3 bg-white/5 hover:bg-white/10 rounded-lg transition border border-white/10">
                                <svg className="w-5 h-5 text-slate-500 hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>
                        
                        <div className="p-10 space-y-0 relative">
                            {/* Vertical Line */}
                            {history.length > 0 && (
                                <div className="absolute left-[3.5rem] top-10 bottom-10 w-0.5 bg-gradient-to-b from-emerald-500/50 via-cyan-500/20 to-transparent"></div>
                            )}
                            
                            {history.length > 0 ? history.map((step, idx) => (
                                <div key={idx} className="relative pl-24 pb-12 last:pb-0 group">
                                    {/* Timeline Marker */}
                                    <div className={`absolute left-[3.15rem] top-0 w-3 h-3 rounded-full border-2 border-[#080a0f] z-10 shadow-lg ${
                                        idx === 0 ? 'bg-emerald-500 shadow-emerald-500/50' : 'bg-slate-700 shadow-slate-900'
                                    }`}></div>
                                    
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                                        <div className="flex items-center gap-4">
                                            <span className={`text-[10px] font-semibold uppercase tracking-widest px-3 py-1.5 rounded-lg border ${
                                                step.value?.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 
                                                step.value?.status === 'retired' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                                step.value?.status === 'rejected' ? 'bg-rose-500/10 text-rose-400 border-rose-500/20' :
                                                'bg-slate-500/10 text-slate-400 border-white/5'
                                            }`}>
                                                {(step.value?.status || 'UNKNOWN').replace('_', ' ')}
                                            </span>
                                            <span className="text-[11px] font-mono text-slate-500 bg-white/5 px-3 py-1 rounded-lg border border-white/5">
                                                {step.timestamp?.includes('seconds:') 
                                                    ? new Date(parseInt(step.timestamp.split('seconds:')[1]) * 1000).toLocaleString()
                                                    : step.timestamp}
                                            </span>
                                        </div>
                                        <div className="text-[9px] font-mono text-slate-600 bg-black/30 px-3 py-1 rounded-lg">
                                            TX: {step.txId?.substring(0, 16)}...
                                        </div>
                                    </div>
                                    
                                    <div className="border border-white/8 bg-white/2 p-6 rounded-xl transition">
                                        <p className="text-sm font-semibold text-slate-200 mb-2 leading-relaxed">
                                            {step.value?.expertDid ? (
                                                <>Validated by <span className="text-emerald-400">Technical Verifier</span> <span className="font-mono text-[11px] opacity-60">({step.value.expertDid.substring(0, 12)}...)</span></>
                                            ) : (
                                                <>Initial <span className="text-cyan-400">Production Attestation</span> synthesized by Generation Meter</>
                                            )}
                                        </p>
                                        {(step.value?.expertNotes || step.value?.notes) && (
                                            <div className="mt-3 pt-3 border-t border-white/5 text-xs text-slate-500 italic">
                                                " {step.value?.expertNotes || step.value?.notes} "
                                            </div>
                                        )}
                                        <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
                                            <div className="bg-black/20 p-3 rounded-lg border border-white/5">
                                                <p className="text-[8px] font-semibold uppercase text-slate-600 mb-1">MWH Yield</p>
                                                <p className="text-xs font-bold text-emerald-500">{step.value?.mwh || step.value?.docType || '--'}</p>
                                            </div>
                                            <div className="bg-black/20 p-3 rounded-lg border border-white/5">
                                                <p className="text-[8px] font-semibold uppercase text-slate-600 mb-1">Asset Status</p>
                                                <p className="text-xs font-bold text-slate-300 capitalize">{step.value?.status || '--'}</p>
                                            </div>
                                            {(step.value?.isPhysical || step.value?.issuer) && (
                                                <div className="bg-black/20 p-3 rounded-lg border border-white/5">
                                                    <p className="text-[8px] font-semibold uppercase text-slate-600 mb-1">Source</p>
                                                    <p className="text-xs font-bold text-blue-400 truncate">{step.value?.issuer || 'IoT Sensor'}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <div className="py-20 text-center">
                                    <p className="text-slate-500 text-sm italic">Synchronizing audit data from peer nodes...</p>
                                    <div className="mt-4 flex justify-center">
                                        <div className="w-8 h-8 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="p-8 bg-[#0a0c12]/50 border-t border-white/10 flex justify-center">
                            <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-2">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                                Verified on Hyperledger Fabric · I-REC Standard · GCC · Evident Registry
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
